// ============================================
// Path: /frontend/src/components/FeaturedListings.jsx
// Purpose: Displays featured vehicle and property listings with rotation
// REPLACE THE ENTIRE FILE WITH THIS
// ============================================

import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { listingsAPI } from '../api/listings.api';
import { bookmarksAPI } from '../api/bookmarks.api';
import { useAuth } from '../hooks/useAuth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ListingCard } from './ListingCard';
import { Skeleton } from './ui/skeleton';

const LoadingSkeleton = () => {
  console.log('[FeaturedListings] Rendering loading skeleton');
  
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-4">
          <Skeleton className="h-48 w-full" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-1/4" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-4 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
};

export function FeaturedListings() {
  console.log('[FeaturedListings] 🎬 Component mounted');

  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(Date.now());
  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  
  const location = useLocation();
  const rotationIntervalRef = useRef(null);

  // Determine default tab based on calendar day
  const getDefaultTab = () => {
    const currentDay = new Date().getDate();
    const isEvenDay = currentDay % 2 === 0;
    const defaultTab = isEvenDay ? 'vehicles' : 'properties';
    console.log(`[FeaturedListings] 📅 Day ${currentDay} - Default tab: ${defaultTab}`);
    return defaultTab;
  };

  const [activeTab, setActiveTab] = useState(getDefaultTab());

  // Fetch featured listings
  const fetchFeaturedListings = async () => {
    console.log('[FeaturedListings] 🔄 Fetching featured listings...');
    setLoading(true);
    
    try {
      const data = await listingsAPI.getFeaturedListings();
      console.log('[FeaturedListings] ✅ Fetched', data.listings?.length || 0, 'listings');
      console.log('[FeaturedListings] 📊 Stats:', data.stats);
      setListings(data.listings || []);
      setError(null);
      setLastFetchTime(Date.now());

      // Batch check bookmarks if user is logged in
      if (user && data.listings?.length > 0) {
        const listingIds = data.listings.map(l => l.id);
        const bookmarkData = await bookmarksAPI.checkBookmarksBatch(listingIds);
        setBookmarkedIds(bookmarkData.bookmarked || []);
        console.log('[FeaturedListings] 📌 Bookmarked IDs:', bookmarkData.bookmarked?.length || 0);
      }
    } catch (err) {
      console.error('[FeaturedListings] ❌ Error fetching listings:', err);
      setError(err.response?.data?.message || 'Failed to load featured listings');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    fetchFeaturedListings();
  }, []);

  // Rotation logic: Fetch on route change (tab switching)
  useEffect(() => {
    console.log('[FeaturedListings] 🔀 Route changed, rotating listings');
    fetchFeaturedListings();
  }, [location.pathname]);

  // Rotation logic: Every 15 minutes
  useEffect(() => {
    // Clear existing interval
    if (rotationIntervalRef.current) {
      clearInterval(rotationIntervalRef.current);
    }

    // Set up 15-minute rotation
    rotationIntervalRef.current = setInterval(() => {
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      console.log('[FeaturedListings] ⏰ 15-minute timer triggered');
      console.log(`[FeaturedListings] 🕐 Time since last fetch: ${Math.floor(timeSinceLastFetch / 1000)}s`);
      fetchFeaturedListings();
    }, 15 * 60 * 1000); // 15 minutes

    // Cleanup on unmount
    return () => {
      if (rotationIntervalRef.current) {
        clearInterval(rotationIntervalRef.current);
      }
    };
  }, [lastFetchTime]);

  // Filter listings by category
  const vehicleListings = listings.filter((l) => l.category === 'VEHICLE').slice(0, 20);
  const propertyListings = listings.filter((l) => l.category === 'PROPERTY').slice(0, 20);

  console.log('[FeaturedListings] 🚗 Vehicle listings:', vehicleListings.length);
  console.log('[FeaturedListings] 🏠 Property listings:', propertyListings.length);

  if (error) {
    console.error('[FeaturedListings] ⚠️ Displaying error state:', error);
    return (
      <div className="text-center py-12 text-destructive">
        Error loading listings: {error}
      </div>
    );
  }

  return (
    <section className="py-6 md:py-10">
      <div className="container">
        <h2 className="text-base sm:text-lg md:text-xl font-normal text-center mb-4">Featured Listings</h2>   
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-8">
            <TabsTrigger 
              value="vehicles"
              onClick={() => {
                console.log('[FeaturedListings] 🚗 Switched to vehicles tab');
                setActiveTab('vehicles');
              }}
            >
              Vehicles
            </TabsTrigger>
            <TabsTrigger 
              value="properties"
              onClick={() => {
                console.log('[FeaturedListings] 🏠 Switched to properties tab');
                setActiveTab('properties');
              }}
            >
              Properties
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="vehicles">
            {loading ? (
              <LoadingSkeleton />
            ) : vehicleListings.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No vehicle listings available yet.
              </p>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 md:gap-4">            
                {vehicleListings.map((listing) => (
                  <ListingCard
                    key={listing.id}
                    id={listing.id}
                    title={listing.title}
                    price={`KES ${listing.price.toLocaleString('en-US')}`}
                    location={`${listing.subCounty}, ${listing.county}`}
                    imageUrl={listing.imageUrl}
                    type={listing.subType || listing.type}
                    isPromoted={listing.isFeatured && new Date(listing.featuredUntil) > new Date()}
                    initialBookmarked={bookmarkedIds.includes(listing.id)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="properties">
            {loading ? (
              <LoadingSkeleton />
            ) : propertyListings.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No property listings available yet.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 md:gap-4">
                {propertyListings.map((listing) => (
                 <ListingCard
                  key={listing.id}
                  id={listing.id}
                  title={listing.title}
                  price={listing.price}
                  location={`${listing.subCounty}, ${listing.county}`}
                  imageUrl={listing.imageUrl}
                  type={listing.subType || listing.type}
                  category={listing.category}
                  isPromoted={listing.isFeatured && new Date(listing.featuredUntil) > new Date()}
                  createdAt={listing.createdAt}
                  allowGroupBuy={listing.allowGroupBuy}
                  chamaInterestCount={listing._count?.chamaInterests || 0}
                  initialBookmarked={bookmarkedIds.includes(listing.id)}
                />
              ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}